import { useEffect, useState, useRef } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import { Check, Mail, Phone, ArrowRight, Copy, ShieldCheck, Smartphone, Building2, CreditCard, Banknote, Facebook, MessageCircle, Loader2, RefreshCw, XCircle, ExternalLink } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, PHP } from "@/lib/api";
import { CONTACT } from "@/lib/contact";
import { reservationFeeFor } from "@/lib/policy";
import { toast } from "sonner";

const QR = {
  paypal: "https://customer-assets.emergentagent.com/job_palawan-wheels/artifacts/erc70vcy_IMG_0216.jpeg",
  bpi: "https://customer-assets.emergentagent.com/job_palawan-wheels/artifacts/ce1t3r0a_IMG_0147.jpeg",
  bdo: "https://customer-assets.emergentagent.com/job_palawan-wheels/artifacts/yl070wwq_IMG_0148.jpeg",
  maya: "https://customer-assets.emergentagent.com/job_palawan-wheels/artifacts/wo97p1jv_IMG_0149.jpeg",
  gcash: "https://customer-assets.emergentagent.com/job_palawan-wheels/artifacts/rl5u8s6n_IMG_0150.jpeg",
};

const PAYMENT_LABEL = {
  gcash: "GCash",
  maya: "Maya",
  bank_transfer: "Bank Transfer",
  paypal: "PayPal",
  card: "Credit / Debit Card",
  cash: "Cash on Pickup",
  paymongo: "Online Payment (Card / GCash / Maya)",
};

function PaymentInstructions({ method }) {
  if (method === "gcash") {
    return (
      <InstructionCard icon={Smartphone} title="GCash Payment Instructions" color="blue">
        <ol className="list-decimal list-inside space-y-2 text-sm text-foreground/80">
          <li>Open your GCash app and tap <span className="text-gold-300">Send Money</span> or <span className="text-gold-300">Scan QR</span>.</li>
          <li>Scan the GCash QR below (account: <span className="text-gold-300">PE***Y B.</span>, mobile <span className="text-gold-300">093• ****062</span>).</li>
          <li>Enter the amount and your booking reference in the message field.</li>
          <li>Send a screenshot of the receipt to <span className="text-gold-300">{CONTACT.whatsapp}</span> on WhatsApp/Viber.</li>
        </ol>
        <QRImage src={QR.gcash} label="GCash QR (InstaPay)" />
      </InstructionCard>
    );
  }
  if (method === "maya") {
    return (
      <InstructionCard icon={Smartphone} title="Maya Payment Instructions" color="green">
        <ol className="list-decimal list-inside space-y-2 text-sm text-foreground/80">
          <li>Open your Maya app and tap <span className="text-gold-300">Send</span> or <span className="text-gold-300">Scan QR</span>.</li>
          <li>Scan the Maya QR below (<span className="text-gold-300">PEARLY BELLONES · @perlietot21</span>).</li>
          <li>Enter the amount and your booking reference as the note.</li>
          <li>Send the receipt screenshot via WhatsApp/Viber: <span className="text-gold-300">{CONTACT.whatsapp}</span>.</li>
        </ol>
        <QRImage src={QR.maya} label="Maya QR (InstaPay)" />
      </InstructionCard>
    );
  }
  if (method === "bank_transfer") {
    return (
      <InstructionCard icon={Building2} title="Bank Transfer Instructions" color="red">
        <ol className="list-decimal list-inside space-y-2 text-sm text-foreground/80">
          <li>Open your banking app (BPI, BDO, or any InstaPay-enabled bank).</li>
          <li>Scan the QR for your bank below or transfer to <span className="text-gold-300">Pearly Bellones</span> (BPI: ******634, BDO: ******4629).</li>
          <li>Enter your booking reference as the note.</li>
          <li>Send the receipt to <span className="text-gold-300">{CONTACT.whatsapp}</span> on WhatsApp/Viber.</li>
        </ol>
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <QRImage src={QR.bpi} label="BPI Bank QR" inline />
          <QRImage src={QR.bdo} label="BDO Bank QR" inline />
        </div>
      </InstructionCard>
    );
  }
  if (method === "paypal") {
    return (
      <InstructionCard icon={CreditCard} title="PayPal Payment Instructions" color="blue">
        <ol className="list-decimal list-inside space-y-2 text-sm text-foreground/80">
          <li>Open the PayPal app and tap <span className="text-gold-300">Scan</span> (or scan the QR with your phone camera).</li>
          <li>Confirm payee is <span className="text-gold-300">Pearly Bellones</span> and enter the amount.</li>
          <li>Conversion fees are charged by PayPal and paid by client.</li>
          <li>Send the transaction ID to <span className="text-gold-300">{CONTACT.whatsapp}</span>.</li>
        </ol>
        <QRImage src={QR.paypal} label="PayPal QR" />
      </InstructionCard>
    );
  }
  if (method === "card") {
    return (
      <InstructionCard icon={CreditCard} title="Card Payment" color="purple">
        <p className="text-sm text-foreground/80 leading-relaxed">
          Card payments are processed at our shop on the day of vehicle release. A <span className="text-gold-300">3.5% merchant fee</span> applies for tax/processing.
          Please still send the <span className="text-gold-300">reservation fee</span> via GCash, Maya, or bank to lock in your booking.
        </p>
      </InstructionCard>
    );
  }
  return (
    <InstructionCard icon={Banknote} title="Cash on Pickup" color="amber">
      <p className="text-sm text-foreground/80 leading-relaxed">
        You'll pay the full amount in cash at our shop on the day of vehicle release.
        Please still send the <span className="text-gold-300">reservation fee</span> via GCash, Maya, or bank to lock in your booking — see your booking email for the QR codes.
      </p>
    </InstructionCard>
  );
}

function InstructionCard({ icon: Icon, title, color, children }) {
  const ring = {
    blue: "border-blue-500/30 bg-gradient-to-br from-blue-500/10 to-blue-900/10",
    green: "border-green-500/30 bg-gradient-to-br from-green-500/10 to-emerald-900/10",
    red: "border-red-500/30 bg-gradient-to-br from-red-500/10 to-yellow-700/10",
    purple: "border-purple-500/30 bg-gradient-to-br from-purple-500/10 to-purple-900/10",
    amber: "border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-amber-900/10",
  };
  return (
    <div className={`rounded-2xl p-6 border ${ring[color]} text-left`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-gold-500/15 border border-gold-500/30 flex items-center justify-center">
          <Icon className="w-4 h-4 text-gold-400" />
        </div>
        <p className="font-display text-2xl text-gold-200">{title}</p>
      </div>
      {children}
    </div>
  );
}

function QRImage({ src, label, inline }) {
  return (
    <div className={`bg-white rounded-xl p-3 ${inline ? "" : "mt-5 max-w-[280px] mx-auto"}`}>
      <img src={src} alt={label} className="w-full h-auto rounded-md" loading="lazy" />
      <p className="text-[10px] uppercase tracking-widest text-ink-900 mt-2 text-center font-medium">{label}</p>
    </div>
  );
}

export default function BookingSuccess() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const pollRef = useRef(null);
  const viaPaymongo = params.get("via") === "paymongo";
  const canceled = params.get("canceled") === "1";

  useEffect(() => {
    api.get(`/bookings/${id}`).then((r) => {
      setB(r.data);
      // Fetch vehicle details for accurate type/category
      if (r.data?.vehicle_id) {
        api.get(`/vehicles/${r.data.vehicle_id}`).then((vr) => setVehicle(vr.data)).catch(() => {});
      }
    }).catch(() => setB(null));
  }, [id]);

  // Poll webhook-verified payment status for PayMongo bookings
  useEffect(() => {
    if (!viaPaymongo || canceled) return;
    let count = 0;
    pollRef.current = setInterval(async () => {
      count += 1;
      try {
        const r = await api.get(`/bookings/${id}/payment-status`);
        setB((prev) => (prev ? { ...prev, ...r.data } : prev));
        if (["paid", "partially_paid", "fully_paid", "payment_failed"].includes(r.data.payment_status) || count > 100) {
          clearInterval(pollRef.current);
        }
      } catch {
        if (count > 100) clearInterval(pollRef.current);
      }
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [id, viaPaymongo, canceled]);

  const retryPayment = async () => {
    // Open window synchronously (inside the click gesture) so popup blockers allow it
    const payWin = window.open("", "_blank");
    if (payWin) {
      try { payWin.opener = null; payWin.document.write("<p style='font-family:sans-serif;padding:24px'>Loading secure PayMongo checkout…</p>"); } catch {}
    }
    try {
      setRetrying(true);
      const r = await api.post("/payments/paymongo/checkout", { booking_id: id });
      // PayMongo blocks iframe embedding — checkout must open in its own tab
      if (payWin) payWin.location.replace(r.data.checkout_url);
      else window.open(r.data.checkout_url, "_blank", "noopener,noreferrer");
      setB((prev) => (prev ? { ...prev, paymongo_checkout_url: r.data.checkout_url, payment_status: "pending" } : prev));
      setRetrying(false);
      navigate(`/booking-success/${id}?via=paymongo`, { replace: true });
    } catch (e) {
      if (payWin) payWin.close();
      toast.error(e?.response?.data?.detail || "Could not restart payment. Please pay via QR transfer or contact us.");
      setRetrying(false);
    }
  };

  const copyRef = () => {
    if (b?.booking_ref) {
      navigator.clipboard.writeText(b.booking_ref);
      toast.success("Booking reference copied");
    }
  };

  if (!b) return <div className="min-h-screen bg-ink-900"><Navbar /></div>;

  // Reservation fee stored at booking creation = one day's rental
  const reservationFee = b.reservation_fee || reservationFeeFor({ daily_rate: b.daily_rate });
  // Security deposit: 0 when customer opted to leave 1 valid ID instead
  const securityDeposit = Number(b.security_deposit || 0);
  const leftId = securityDeposit === 0;
  const isMotorbike = vehicle ? vehicle.type === "motorbike" : false;
  const grandTotal = (b.grand_total) || (Number(b.total || 0) + Number(securityDeposit));
  const isPaid = ["paid", "partially_paid", "fully_paid"].includes(b.payment_status);
  const pendingVerification = b.payment_status === "pending_verification";
  const payFailed = canceled || b.payment_status === "payment_failed";
  const isPaymongo = b.payment_method === "paymongo";
  const scopeFull = b.payment_scope === "full";
  const dueNow = Number(b.amount_due_now || reservationFee);
  const paidAmount = isPaid ? Number(b.amount_paid || dueNow) : 0;
  const payableAtPickup = Math.max(0, grandTotal - (isPaid ? paidAmount : dueNow));

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="hero-bg pt-32 pb-20 noise" data-testid="booking-success-page">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center">
            {isPaid ? (
              <>
                <div className="w-20 h-20 mx-auto rounded-full bg-green-500/15 border border-green-500/40 flex items-center justify-center">
                  <Check className="w-10 h-10 text-green-400" />
                </div>
                <p className="kicker justify-center mt-8">Payment Verified</p>
                <h1 className="font-display text-5xl mt-4">Booking Confirmed.</h1>
                <p className="mt-4 text-foreground/65 max-w-xl mx-auto" data-testid="paid-reserved-banner">
                  Your reservation fee is <span className="text-green-300 font-medium">PAID</span> and your vehicle is <span className="text-green-300 font-medium">RESERVED</span>. A confirmation email is on its way.
                </p>
              </>
            ) : payFailed && isPaymongo ? (
              <>
                <div className="w-20 h-20 mx-auto rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center">
                  <XCircle className="w-10 h-10 text-red-400" />
                </div>
                <p className="kicker justify-center mt-8">Payment Incomplete</p>
                <h1 className="font-display text-4xl sm:text-5xl mt-4">Payment Was Not Completed.</h1>
                <p className="mt-4 text-foreground/65 max-w-xl mx-auto" data-testid="payment-failed-banner">
                  Your reservation has <span className="text-red-300 font-medium">not been confirmed</span>. You can retry the secure payment, or pay via GCash/Maya/Bank QR instead.
                </p>
                <button onClick={retryPayment} disabled={retrying} className="btn-gold mt-6 disabled:opacity-60" data-testid="retry-payment">
                  {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Retry Payment
                </button>
              </>
            ) : viaPaymongo && isPaymongo ? (
              <>
                <div className="w-20 h-20 mx-auto rounded-full bg-gold-500/15 border border-gold-500/40 flex items-center justify-center">
                  <Loader2 className="w-10 h-10 text-gold-400 animate-spin" />
                </div>
                <p className="kicker justify-center mt-8">Verifying Payment</p>
                <h1 className="font-display text-4xl sm:text-5xl mt-4">Complete Your Payment.</h1>
                <p className="mt-4 text-foreground/65 max-w-xl mx-auto" data-testid="payment-verifying-banner">
                  The secure payment page opened in a <span className="text-gold-300 font-medium">new browser tab</span>. If it didn't open, use the button below. This page updates automatically once your payment is verified.
                </p>
                {b.paymongo_checkout_url && (
                  <a
                    href={b.paymongo_checkout_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-gold mt-6 inline-flex"
                    data-testid="open-payment-page"
                  >
                    <ExternalLink className="w-4 h-4" /> Open Secure Payment Page
                  </a>
                )}
              </>
            ) : (
              <>
                <div className="w-20 h-20 mx-auto rounded-full bg-gold-500/15 border border-gold-500/40 flex items-center justify-center">
                  <Check className="w-10 h-10 text-gold-400" />
                </div>
                <p className="kicker justify-center mt-8">{pendingVerification ? "Payment Under Verification" : "Booking Received"}</p>
                <h1 className="font-display text-5xl mt-4">Welcome to the Drive.</h1>
                <p className="mt-4 text-foreground/65 max-w-xl mx-auto">
                  {pendingVerification
                    ? "Thank you! Our team is verifying your payment — your booking will be confirmed once the transfer is verified. Please send your receipt via WhatsApp or email to speed this up."
                    : "Thank you for choosing PB-Lux. Send the payment using the instructions below to confirm your booking."}
                </p>
              </>
            )}
          </div>

          {/* Booking summary */}
          <div className="mt-10 glass-card rounded-2xl p-7 text-left">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-foreground/55">Booking Reference</p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="font-display text-3xl text-gold-300" data-testid="booking-ref">{b.booking_ref}</p>
                  <button onClick={copyRef} className="text-gold-400 hover:text-gold-200" data-testid="copy-ref"><Copy className="w-4 h-4" /></button>
                </div>
              </div>
              <span className="spec-pill capitalize">{b.booking_status}</span>
            </div>

            <div className="gold-divider my-6" />

            <div className="grid sm:grid-cols-2 gap-5 text-sm">
              <Field l="Vehicle" v={b.vehicle_name} />
              <Field l="Days" v={`${b.days} ${b.days === 1 ? "day" : "days"}`} />
              <Field l="Pickup" v={new Date(b.pickup_date).toLocaleDateString()} />
              <Field l="Return" v={new Date(b.return_date).toLocaleDateString()} />
              <Field l="Pickup Location" v={b.pickup_location} />
              <Field l="Drop-off" v={b.dropoff_address || (b.dropoff_location_type === "el_nido" ? "El Nido" : b.dropoff_location_type === "ppc_hotel" ? "Hotel Collection (PPC)" : "PB-Lux Shop, Puerto Princesa")} />
              {b.country && <Field l="Country" v={b.country} />}
              <Field l="Payment Method" v={PAYMENT_LABEL[b.payment_method] || b.payment_method.replace("_"," ")} />
              <Field l="Payment Status" v={isPaid ? (b.payment_status === "fully_paid" ? "FULLY PAID" : "PAID / RESERVED") : pendingVerification ? "Under verification" : b.payment_status === "payment_failed" ? "Payment failed" : "Pending"} green={isPaid} />
              {b.discount_percent > 0 && (
                <Field l="Loyalty Discount" v={`${b.discount_percent}% off · saved ${PHP(b.discount_amount)}`} green />
              )}
              <Field l="Rental Cost" v={PHP(b.total)} />
              <Field l={leftId ? "Security Option" : "Refundable Security Deposit"} v={leftId ? "Passport (subject to verification)" : PHP(securityDeposit)} />
              <Field l="Grand Total" v={PHP(grandTotal)} highlight colspan />
              <Field l={isPaid ? "Reservation Fee — PAID ✓" : `Reservation Fee (${PAYMENT_LABEL[b.payment_method] || ""})`} v={`− ${PHP(reservationFee)}`} green />
              <Field l="Remaining Balance on Pickup" v={PHP(payableAtPickup)} highlight />
            </div>
          </div>

          {/* Reservation fee + Security Deposit highlight */}
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl p-6 bg-gradient-to-br from-gold-500/15 to-gold-700/10 border border-gold-500/40" data-testid="reservation-fee-card">
              <p className="kicker">{scopeFull ? "Full Payment" : "Reservation Fee"}{isPaid ? " — Paid ✓" : ""}</p>
              <p className="font-display text-4xl text-gold-200 mt-3">{PHP(scopeFull ? dueNow : reservationFee)}</p>
              <p className="text-xs text-foreground/65 mt-2">
                {scopeFull
                  ? <>Covers your entire rental charge. <span className="text-gold-300">Security deposit (if any) is separate</span>, payable at pickup.</>
                  : <>Equivalent to <span className="text-gold-300">one day's rental</span> of {b.vehicle_name}. Deductible from total.</>}{" "}
                {isPaid
                  ? "Payment verified — your unit is reserved."
                  : isPaymongo
                  ? "Pay securely online via Card / GCash / Maya using the payment button above."
                  : "Send via GCash, Maya, Bank, or PayPal within 24 hours to confirm."}
              </p>
            </div>
            <div className="rounded-2xl p-6 bg-gradient-to-br from-royal-700/25 to-royal-900/15 border border-royal-500/40" data-testid="security-deposit-card">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-gold-400" />
                <p className="kicker !before:hidden m-0">{leftId ? "Security: Passport Option" : "Refundable Security Deposit"}</p>
              </div>
              {leftId ? (
                <>
                  <p className="font-display text-4xl text-gold-200 mt-3">Passport</p>
                  <p className="text-xs text-foreground/65 mt-2">
                    You chose to provide a <span className="text-gold-300">valid passport as security</span> — no cash deposit added.
                    The passport and your eligibility are verified and approved before vehicle release, and returned when the unit comes back.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-display text-4xl text-gold-200 mt-3">{PHP(securityDeposit)}</p>
                  <p className="text-xs text-foreground/65 mt-2">
                    {isMotorbike ? "Motorbike (₱3,000)" : "Car (₱5,000)"} refundable security deposit, payable on pickup.
                    Refunded after vehicle return and inspection, subject to the rental agreement.
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Payment instructions specific to method (manual methods only) */}
          {!isPaymongo && (
            <div className="mt-8" data-testid="payment-instructions-success">
              <PaymentInstructions method={b.payment_method} />
            </div>
          )}

          {/* Next steps */}
          <div className="mt-6 glass-card rounded-2xl p-6 text-sm text-foreground/75 leading-relaxed">
            <p className="kicker">Final Checklist</p>
            <ul className="mt-4 space-y-2.5 list-disc list-inside marker:text-gold-500">
              <li>{isPaid
                ? <>{scopeFull ? "Full payment" : "Reservation fee"} <span className="text-green-300">paid ✓</span> — your unit is reserved.</>
                : isPaymongo
                ? <>Complete the <span className="text-gold-200">secure online payment of {PHP(dueNow)}</span> to confirm your booking.</>
                : <>Send the <span className="text-gold-200">{scopeFull ? "full payment" : "reservation fee"} of {PHP(dueNow)}</span> within 24 hours.</>}</li>
              <li>Bring on pickup: <span className="text-gold-200">Driver's License + 1 valid government ID</span></li>
              <li><span className="text-gold-200">Payable at pickup: {PHP(payableAtPickup)}</span>{leftId ? "" : ` (includes refundable ${PHP(securityDeposit)} deposit)`}.</li>
              <li>{scopeFull ? "Your full payment covers the rental" : "The amount paid is deducted from your Grand Total"} — the refundable deposit {leftId ? "is replaced by your passport/ID security" : "is paid separately at pickup and returned after inspection"}.</li>
              <li>Our team will message you on <span className="text-gold-300">{b.customer_phone}</span> to coordinate pickup.</li>
            </ul>
          </div>

          {/* Quick contact */}
          <div className="mt-8 text-center">
            <p className="text-sm text-foreground/55 mb-4">Questions? Reach us in seconds:</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <a href={CONTACT.facebookUrl} target="_blank" rel="noreferrer" className="btn-outline-gold !text-[11px]">
                <Facebook className="w-4 h-4" /> Messenger
              </a>
              <a href={`https://wa.me/${CONTACT.whatsappRaw.replace("+", "")}`} target="_blank" rel="noreferrer" className="btn-outline-gold !text-[11px]">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <a href={`tel:${CONTACT.phoneRaw}`} className="btn-outline-gold !text-[11px]">
                <Phone className="w-4 h-4" /> {CONTACT.phone}
              </a>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/my-bookings" className="btn-gold" data-testid="view-my-bookings">
              View My Bookings <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={`mailto:${CONTACT.email}`} className="btn-outline-gold">
              <Mail className="w-4 h-4" /> Email Us
            </a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function Field({ l, v, highlight, colspan, green }) {
  return (
    <div className={colspan ? "sm:col-span-2" : ""}>
      <p className="text-[10px] uppercase tracking-widest text-foreground/50">{l}</p>
      <p className={`mt-1 ${highlight ? "font-display text-2xl text-gold-300" : green ? "text-green-300 font-medium" : "text-foreground"}`}>{v}</p>
    </div>
  );
}
